import React, { ForwardedRef, Ref } from 'react'

import { gql } from '@apollo/client'
import classNames from 'classnames'

import { ThumbnailRatio, getThumbnailRatio } from '@local/utils'

import Clickable from '@web/components/Clickable'
import { FlickingImageItem_ImageItemFragment, FlickingImageItem_MediaItemFragment } from '@web/gql/graphql'
import { getThumbnailTypeByRatio } from '@web/libs/media'
import photoInfra from '@web/libs/media/photoInfra'

import Image from '../Image'
import styles from './FlickingItem.module.scss'

interface Props {
  imageItem: FlickingImageItem_MediaItemFragment & FlickingImageItem_ImageItemFragment
  idx: number
  nClickCode?: string
  openImageViewer: (idx: number) => void
  isMulti?: boolean
  isTheme?: boolean
  isThemeMap: boolean
  useLazyLoading?: boolean
  isPlaybackError?: boolean
  highestRatio?: ThumbnailRatio
  onClick?: () => void
}

const FlickingImageItem = (
  {
    imageItem,
    idx,
    openImageViewer,
    nClickCode,
    isMulti = false,
    isTheme = false,
    isThemeMap = false,
    useLazyLoading = false,
    highestRatio,
    onClick,
  }: Props,
  ref?: ForwardedRef<HTMLDivElement>,
) => {
  const ratio = getThumbnailRatio(imageItem)
  const thumbnailType = isThemeMap
    ? photoInfra.type.w828_60_sharpen
    : getThumbnailTypeByRatio({ ratio, fixedBy: 'width' })

  return (
    <Clickable
      ref={ref as Ref<HTMLButtonElement>}
      className={classNames(styles.thumb_wrapper, {
        [styles.type_multi]: isMulti,
        [styles.type_theme]: isTheme,
        [styles.type_theme_map]: isThemeMap,
        [styles.has_portrait]: !isThemeMap && highestRatio === ThumbnailRatio.PORTRAIT,
        [styles.has_square]: !isThemeMap && highestRatio === ThumbnailRatio.SQUARE,
        [styles.has_landscape]: !isThemeMap && highestRatio === ThumbnailRatio.LANDSCAPE,
      })}
      onClick={() => (onClick ? onClick() : openImageViewer(idx))}
      nClickCode={nClickCode}
    >
      <span className={styles.thumb_blur_wrapper}>
        <Image className={styles.thumb_blur} src={imageItem.url} alt="" thumbnailType={photoInfra.type.f50_blur} />
      </span>
      <Image
        className={classNames(styles.thumb, {
          [styles.portrait]: ratio === ThumbnailRatio.PORTRAIT,
          [styles.landscape]: ratio === ThumbnailRatio.LANDSCAPE,
          [styles.square]: ratio === ThumbnailRatio.SQUARE,
        })}
        src={imageItem.url}
        width="100%"
        alt=""
        lazyLoading={useLazyLoading}
        thumbnailType={thumbnailType}
      />
    </Clickable>
  )
}

FlickingImageItem.fragments = gql`
  fragment FlickingImageItem_MediaItem on MediaItem {
    id
    type
    width
    height
    ... on ImageItem {
      ...FlickingImageItem_ImageItem
    }
  }
  fragment FlickingImageItem_ImageItem on ImageItem {
    url
  }
`

export default React.forwardRef(FlickingImageItem)
